import React, { useState, useEffect } from 'react';
import { X, ArrowLeft, ArrowRight, RotateCcw, CheckCircle, Award, Sparkles, UserCheck } from 'lucide-react';
import questionsData from '../../docs/data/questions_data.json';
import treeRulesData from '../../docs/data/tree_rules_data.json';

const DOMAIN_INDEX = { STEM: 0, ENG: 1, AI: 2, HUM: 3 };
const RESUME_KEY = 'gradelynk_quiz_progress';

export function CareerMatcherModal({ isOpen, onClose, onSelectMentor }) {
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [report, setReport] = useState(null);

  // Keyboard Escape Key Trap & Focus Management
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Check saved progress on mount / open
  useEffect(() => {
    if (isOpen) {
      const saved = sessionStorage.getItem(RESUME_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed && parsed.currentStep > 0 && parsed.currentStep < 30) {
            setShowResumePrompt(true);
          }
        } catch {
          sessionStorage.removeItem(RESUME_KEY);
        }
      }
    }
  }, [isOpen]);

  // Persist session state
  useEffect(() => {
    if (isOpen && currentStep > 0 && currentStep < 30 && !report) {
      sessionStorage.setItem(RESUME_KEY, JSON.stringify({ currentStep, answers }));
    }
  }, [isOpen, currentStep, answers, report]);

  if (!isOpen) return null;

  const handleResume = () => {
    const saved = JSON.parse(sessionStorage.getItem(RESUME_KEY));
    setAnswers(saved.answers || {});
    setCurrentStep(saved.currentStep || 0);
    setShowResumePrompt(false);
  };

  const handleStartFresh = () => {
    sessionStorage.removeItem(RESUME_KEY);
    setAnswers({});
    setCurrentStep(0);
    setSelectedOption(null);
    setReport(null);
    setShowResumePrompt(false);
  };

  const handleSelectOptionIndex = (optIdx) => {
    if (selectedOption === optIdx) {
      confirmAnswer(optIdx);
    } else {
      setSelectedOption(optIdx);
    }
  };

  const confirmAnswer = (optIdx) => {
    const nextOpt = optIdx !== undefined ? optIdx : selectedOption;
    if (nextOpt === null || nextOpt === undefined) return;

    const newAnswers = { ...answers, [currentStep]: nextOpt };
    setAnswers(newAnswers);
    setSelectedOption(null);

    if (currentStep < 29) {
      setCurrentStep(currentStep + 1);
    } else {
      calculateAndFinish(newAnswers);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setSelectedOption(answers[prevStep] !== undefined ? answers[prevStep] : null);
    }
  };

  const calculateAndFinish = (finalAnswers) => {
    setIsCalculating(true);
    setTimeout(() => {
      const rawVector = [0, 0, 0, 0];
      questionsData.forEach((q, qIdx) => {
        const chosenOptIdx = finalAnswers[qIdx];
        if (chosenOptIdx !== undefined && q.options[chosenOptIdx]) {
          const weights = q.options[chosenOptIdx].weights;
          weights.forEach((w, i) => { rawVector[i] += w; });
        }
      });

      const total = rawVector.reduce((a, b) => a + b, 0);
      let normVec = [0.25, 0.25, 0.25, 0.25];
      let cs = 0.0;
      let compat = 50.0;

      if (total > 0) {
        normVec = rawVector.map(x => Number((x / total).toFixed(4)));
        const sorted = [...normVec].map((val, i) => ({ val, i })).sort((a, b) => b.val - a.val);
        const sPrimary = sorted[0].val;
        const sSecondary = sorted[1].val;

        if (sPrimary === sSecondary) {
          cs = 0.0;
          compat = 50.0;
        } else {
          cs = Number(((sPrimary - sSecondary) * 100).toFixed(1));
          compat = Number((50.0 + (cs * 0.50)).toFixed(1));
        }
      }

      let matchedNode = treeRulesData.find(n => n.node_id === 'N17');

      if (cs > 0.0 && Math.max(...normVec) >= 0.30) {
        for (const node of treeRulesData) {
          if (node.node_id === 'N17') continue;
          const conditions = node.threshold_struct.conditions;
          const isMatch = conditions.every(cond => {
            const val = normVec[DOMAIN_INDEX[cond.domain]];
            if (cond.op === '>=') return val >= cond.value - 1e-9;
            if (cond.op === '<') return val < cond.value - 1e-9;
            return false;
          });
          if (isMatch) {
            matchedNode = node;
            break;
          }
        }
      }

      setReport({
        scores_raw: rawVector,
        scores_normalized: normVec,
        confidence_score: cs,
        compatibility_percentage: compat,
        matched_node_id: matchedNode.node_id,
        category_name: matchedNode.category_name,
        lead_mentor: matchedNode.lead_mentor,
        co_mentor: matchedNode.co_mentor,
        is_hybrid: cs < 30.0 && matchedNode.node_id !== 'N17'
      });

      sessionStorage.removeItem(RESUME_KEY);
      setIsCalculating(false);
    }, 600);
  };

  const currentQ = questionsData[currentStep];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md" role="dialog" aria-modal="true" aria-labelledby="quiz-modal-title">
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100">
        
        {/* Header Bar */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/50 px-6 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <h2 id="quiz-modal-title" className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
              Academic & Mentor Matcher Engine
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close Diagnostic Modal" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white transition cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6">
          {showResumePrompt ? (
            <div className="py-8 text-center space-y-6">
              <div className="inline-flex p-3 rounded-full bg-amber-500/10 text-amber-500 mb-2">
                <RotateCcw className="h-8 w-8" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Resume Diagnostic Session?</h3>
              <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                You have saved progress at Question {currentStep + 1} of 30. Would you like to resume or start fresh?
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <button onClick={handleResume} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold transition cursor-pointer">
                  Resume Session (Q{currentStep + 1})
                </button>
                <button onClick={handleStartFresh} className="w-full sm:w-auto px-6 py-3 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200 transition cursor-pointer">
                  Start Fresh Diagnostic
                </button>
              </div>
            </div>
          ) : isCalculating ? (
            <div className="py-16 text-center space-y-4">
              <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-amber-500 border-t-transparent"></div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Executing 17-Node Matcher Algorithm...</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Evaluating L1 vector normalization and confidence matrix.</p>
            </div>
          ) : report ? (
            <div className="space-y-6">
              <div className="rounded-xl border border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-6 text-center space-y-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-300 text-xs font-semibold uppercase tracking-wider">
                  Matched Category Node: {report.matched_node_id}
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">{report.category_name}</h3>
                <p className="text-slate-700 dark:text-slate-300 text-sm max-w-lg mx-auto">
                  Based on your cognitive diagnostic profile, your highest affinity matches {report.category_name}.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/50 p-4">
                  <div className="text-xs text-slate-500 dark:text-slate-400">Match Compatibility</div>
                  <div className="text-3xl font-extrabold text-amber-600 dark:text-amber-400">{report.compatibility_percentage}%</div>
                  <div className="w-full bg-slate-200 dark:bg-slate-800 h-2 rounded-full mt-2 overflow-hidden">
                    <div className="bg-amber-500 h-full transition-all" style={{ width: `${report.compatibility_percentage}%` }}></div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/50 p-4">
                  <div className="text-xs text-slate-500 dark:text-slate-400">Confidence Score (CS)</div>
                  <div className="text-3xl font-extrabold text-slate-900 dark:text-white">{report.confidence_score}%</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {report.is_hybrid ? 'Hybrid Profile Detected (Multi-Domain)' : 'Single High Dominance Domain'}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950 p-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Recommended Mentor Pairing</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-800 flex items-center gap-3">
                    <UserCheck className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-slate-500">Lead Mentor</div>
                      <div className="font-semibold text-slate-900 dark:text-white">{report.lead_mentor}</div>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-800 flex items-center gap-3">
                    <Award className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-slate-500">Co-Mentor</div>
                      <div className="font-semibold text-slate-900 dark:text-white">{report.co_mentor}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                <button onClick={handleStartFresh} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition cursor-pointer">
                  <RotateCcw className="h-4 w-4" /> Retake Diagnostic
                </button>
                <button onClick={() => { onClose(); if (onSelectMentor) onSelectMentor(report.lead_mentor); }} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold transition cursor-pointer">
                  Connect with Matched Mentor
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span className="font-semibold text-amber-600 dark:text-amber-400">{currentQ.block}</span>
                  <span>Question {currentStep + 1} of 30</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                  <div className="h-full bg-amber-500 transition-all duration-300" style={{ width: `${((currentStep + 1) / 30) * 100}%` }}></div>
                </div>
              </div>

              <h3 className="text-xl font-bold text-slate-900 dark:text-white min-h-[56px] leading-snug">
                {currentQ.question}
              </h3>

              <div className="space-y-3">
                {currentQ.options.map((opt, optIdx) => {
                  const isSelected = selectedOption === optIdx || answers[currentStep] === optIdx;
                  return (
                    <button
                      key={optIdx}
                      onClick={() => handleSelectOptionIndex(optIdx)}
                      className={`w-full text-left p-4 rounded-xl border transition flex items-start justify-between cursor-pointer ${
                        isSelected
                          ? 'border-amber-500 bg-amber-500/10 text-slate-900 dark:text-white'
                          : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300 dark:hover:border-slate-700'
                      }`}
                    >
                      <span className="text-sm leading-normal">
                        <span className="font-semibold text-amber-600 dark:text-amber-400 mr-2">{opt.label}</span>
                        {opt.text}
                      </span>
                      {isSelected && <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 ml-2 mt-0.5" />}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg border transition ${
                    currentStep === 0
                      ? 'border-transparent text-slate-400 cursor-not-allowed'
                      : 'border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white cursor-pointer'
                  }`}
                >
                  <ArrowLeft className="h-4 w-4" /> Previous
                </button>

                <div className="text-xs text-slate-500 hidden sm:block">
                  Tap option twice or click Next to confirm
                </div>

                <button
                  onClick={() => confirmAnswer()}
                  disabled={selectedOption === null && answers[currentStep] === undefined}
                  className={`flex items-center gap-2 text-sm font-bold px-6 py-2.5 rounded-xl transition ${
                    selectedOption !== null || answers[currentStep] !== undefined
                      ? 'bg-amber-500 hover:bg-amber-400 text-slate-950 cursor-pointer'
                      : 'bg-slate-200 text-slate-400 dark:bg-slate-800 dark:text-slate-500 cursor-not-allowed'
                  }`}
                >
                  {currentStep === 29 ? 'Finish Diagnostic' : 'Next'} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
